import * as Yup from 'yup';
import { addYears, startOfToday } from 'date-fns';

import { validationMessages } from '../messages/validationMessages';
import {
    ADDRESS_FULLNAME,
    ADDRESS_CITY,
    ADDRESS_POSTCODE,
    ADDRESS_STATE,
    ADDRESS_STREET1,
    ADDRESS_STREET2,
    FIRST_NAME_MAX_LENGTH,
    LAST_NAME_MAX_LENGTH,
    CARD_NAME_MAX_LENGTH,
} from '../../../address';
import {
    AMOUNT_FIELD,
    BRAND_CODE,
    IS_SELF_BUY,
    MESSAGE_FIELD,
    QUANTITY_FIELD,
    EXPECT_A_GIFT,
    SENDER_NAME_FIELD,
    FACEPLATE_CODE,
    IS_SAVED_ADDRESS,
    ADDRESS_HASH_KEY,
    DELIVERY_DATE_FIELD,
    RECIPIENT_NAME_FIELD,
    RECIPIENT_EMAIL_FIELD,
    DELIVERY_METHOD_FIELD,
    MESSAGE_MAX_LENGTH,
    LINE_ITEM_MAX_QUANTITY
} from '../fields';
import { productShape } from '../../../products/productYup';
import { fullFirstAndLastMiddleOptional, notPoBoxRegEx } from '../../../address/addressValidation';

export const getValidationSchema = (formatMessage, availableAmount) =>
    Yup.object().shape({
        userPoints: Yup.boolean().default(false),
        product: productShape,
        [BRAND_CODE]: Yup.string().required(),
        [IS_SELF_BUY]: Yup.boolean().default(false),
        [EXPECT_A_GIFT]: Yup.boolean().default(false),
        [MESSAGE_FIELD]: Yup.string().max(MESSAGE_MAX_LENGTH),
        [DELIVERY_METHOD_FIELD]: Yup.string().oneOf(['DIGITAL', 'PHYSICAL']),
        [FACEPLATE_CODE]: Yup.string().required(formatMessage(validationMessages.faceplateRequired)),
        [QUANTITY_FIELD]: Yup.number()
            .default(1)
            .max(
                LINE_ITEM_MAX_QUANTITY,
                formatMessage(validationMessages.quantityMax, { max: LINE_ITEM_MAX_QUANTITY })
            ),
        [AMOUNT_FIELD]: Yup.number()
            .transform(function castToInt(currentValue, originalValue) {
                if (this.isType(currentValue)) {
                    return currentValue;
                }
                const cleanString = String(originalValue).replace(/\D/g, '');
                return cleanString ? parseInt(cleanString, 10) : 0;
            })
            .min(Yup.ref('product.baseValue'), ({min}) => formatMessage(validationMessages.amountMin, {min}))
            .when('userPoints', {
                is: true,
                then: Yup.number().max(availableAmount, formatMessage(validationMessages.amountMax, {max: availableAmount})),
                otherwise: Yup.number().max(Yup.ref('product.maxValue'), ({max}) => formatMessage(validationMessages.amountMax, {max}))
            })
            .required(formatMessage(validationMessages.amountRequired)),
        [RECIPIENT_EMAIL_FIELD]: Yup.string().when(['deliveryMethod', 'isSelfBuy', 'expectAGift'], {
            is: (deliveryMethod, isSelfBuy, expectAGift) =>
                (deliveryMethod === 'DIGITAL' && !isSelfBuy) || (deliveryMethod === 'PHYSICAL' && expectAGift),
            then: Yup.string()
                .email(formatMessage(validationMessages.invalidEmailAddress))
                .required(formatMessage(validationMessages.recipientEmailRequired)),
            otherwise: Yup.string().notRequired()
        }),
        [SENDER_NAME_FIELD]: Yup.string().when(['isSelfBuy'], {
            is: isSelfBuy => !isSelfBuy,
            then: Yup.string().required(formatMessage(validationMessages.yourNameRequired)),
            otherwise: Yup.string().notRequired()
        }),
        
        [RECIPIENT_NAME_FIELD]: Yup.string().when(['deliveryMethod', 'isSelfBuy'], {
            is: (deliveryMethod, isSelfBuy) => deliveryMethod === 'DIGITAL' && !isSelfBuy,
            then: Yup.string().required(formatMessage(validationMessages.recipientNameRequired)),
            otherwise: Yup.string().notRequired()
        }),
        [DELIVERY_DATE_FIELD]: Yup.date()
            .transform(function dateCheck(value) {
                return this.isType(value) ? value : startOfToday();
            })
            .when(['deliveryMethod', 'isSelfBuy'], {
                is: (deliveryMethod, isSelfBuy) => deliveryMethod === 'DIGITAL' && !isSelfBuy,
                then: Yup.date()
                    .required(formatMessage(validationMessages.dateRequired))
                    .min(startOfToday(), formatMessage(validationMessages.dateBelowMinimum))
                    .max(addYears(startOfToday(), 1), formatMessage(validationMessages.dateAboveMaximum)),
                otherwise: Yup.date().notRequired()
            }),
        
        [IS_SAVED_ADDRESS]: Yup.boolean(),
        [ADDRESS_HASH_KEY]: Yup.string().when(['deliveryMethod', 'isSavedAddress'], {
            is: (deliveryMethod, isSavedAddress) => deliveryMethod === 'PHYSICAL' && isSavedAddress,
            then: Yup.string().required(formatMessage(validationMessages.savedAddressRequired)),
            otherwise: Yup.string().notRequired()
        }),
        [ADDRESS_FULLNAME]: Yup.string()
            .when(['deliveryMethod', 'isSelfBuy'], {
                is: (deliveryMethod, isSelfBuy) => deliveryMethod === 'PHYSICAL' && !isSelfBuy,
                then: Yup.string()
                    .required(formatMessage(validationMessages.recipientNameRequired))
                    .matches(fullFirstAndLastMiddleOptional, formatMessage(validationMessages.firstAndLastOnly))
                    .test(
                        'firstName-too-long',
                        formatMessage(validationMessages.firstNameTooLong, { max: FIRST_NAME_MAX_LENGTH }),
                        value =>
                            value &&
                            value.match(fullFirstAndLastMiddleOptional) &&
                            value.match(fullFirstAndLastMiddleOptional)[1].length <= FIRST_NAME_MAX_LENGTH
                    )
                    .test(
                        'lastName-too-long',
                        formatMessage(validationMessages.lastNameTooLong, { max: LAST_NAME_MAX_LENGTH }),
                        value =>
                            value &&
                            value.match(fullFirstAndLastMiddleOptional) &&
                            value.match(fullFirstAndLastMiddleOptional)[2].length <= LAST_NAME_MAX_LENGTH
                    )
                    .test(
                        'fullName-too-long',
                        formatMessage(validationMessages.fullNameTooLong, { max: CARD_NAME_MAX_LENGTH }),
                        value =>
                            value &&
                            value.match(fullFirstAndLastMiddleOptional) &&
                            value.match(fullFirstAndLastMiddleOptional).length <= CARD_NAME_MAX_LENGTH
                    ),
                otherwise: Yup.string().notRequired()
            })
            .when(['deliveryMethod', 'isSelfBuy'], {
                is: (deliveryMethod, isSelfBuy) => deliveryMethod === 'PHYSICAL' && isSelfBuy,
                then: Yup.string()
                    .required(formatMessage(validationMessages.yourNameRequired))
                    .matches(fullFirstAndLastMiddleOptional, formatMessage(validationMessages.firstAndLastOnly))
                    .test(
                        'firstName-too-long',
                        formatMessage(validationMessages.firstNameTooLong, { max: FIRST_NAME_MAX_LENGTH }),
                        value =>
                            value &&
                            value.match(fullFirstAndLastMiddleOptional) &&
                            value.match(fullFirstAndLastMiddleOptional)[1].length <= FIRST_NAME_MAX_LENGTH
                    )
                    .test(
                        'lastName-too-long',
                        formatMessage(validationMessages.lastNameTooLong, { max: LAST_NAME_MAX_LENGTH }),
                        value =>
                            value &&
                            value.match(fullFirstAndLastMiddleOptional) &&
                            value.match(fullFirstAndLastMiddleOptional)[2].length <= LAST_NAME_MAX_LENGTH
                    )
                    .test(
                        'fullName-too-long',
                        formatMessage(validationMessages.fullNameTooLong, { max: CARD_NAME_MAX_LENGTH }),
                        value =>
                            value &&
                            value.match(fullFirstAndLastMiddleOptional) &&
                            value.match(fullFirstAndLastMiddleOptional).length <= CARD_NAME_MAX_LENGTH
                    ),
                otherwise: Yup.string().notRequired()
            }),
        [ADDRESS_STREET1]: Yup.string().when('deliveryMethod', {
            is: 'PHYSICAL',
            then: Yup.string()
                .min(3, formatMessage(validationMessages.street1TooShort, { min: 3 }))
                .matches(notPoBoxRegEx, formatMessage(validationMessages.poBoxInvalid))
                .required(formatMessage(validationMessages.addressRequired)),
            otherwise: Yup.string().notRequired()
        }),
        [ADDRESS_STREET2]: Yup.string()
            .notRequired()
            .matches(notPoBoxRegEx, formatMessage(validationMessages.poBoxInvalid)),
        [ADDRESS_CITY]: Yup.string().when('deliveryMethod', {
            is: 'PHYSICAL',
            then: Yup.string()
                .min(2)
                .required(formatMessage(validationMessages.cityRequired)),
            otherwise: Yup.string().notRequired()
        }),
        [ADDRESS_STATE]: Yup.string().when('deliveryMethod', {
            is: 'PHYSICAL',
            then: Yup.string()
                .length(2)
                .required(formatMessage(validationMessages.stateRequired)),
            otherwise: Yup.string().notRequired()
        }),
        [ADDRESS_POSTCODE]: Yup.string().when('deliveryMethod', {
            is: 'PHYSICAL',
            then: Yup.string()
                .matches(/^[0-9]{5}(?:-[0-9]{4})?$/)
                .required(formatMessage(validationMessages.postalCodeRequired)),
            otherwise: Yup.string().notRequired()
        }),
        lineItemsCount: Yup.number()
            .max(14, formatMessage(validationMessages.maxLineItems))
            .required(),
        canShip: Yup.boolean().when('deliveryMethod', {
            is: 'PHYSICAL',
            then: Yup.boolean()
                .default(false)
                .test('can-ship', formatMessage(validationMessages.canShipValid), value => value === true),
            otherwise: Yup.boolean().notRequired()
        })
    });
